import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { FiSend, FiUser, FiFileText, FiCheck, FiAlertTriangle, FiMail } from 'react-icons/fi';
import { dataFetch } from '../../api';
import { learners } from '../../data/learners';
import { trainingCampaigns } from '../../data/trainingCampaigns';
import { notificationTemplates } from '../../data/notificationTemplates';
import { sendBulkEmails, buildEmailFromTemplate } from '../../services/emailService';
import { sendBulkReminderEmails } from '../../services/resendemailService';

const SendReminderPanel = () => {
  const [form, setForm] = useState({
    audience:   'all_overdue',
    learnerId:  '',
    campaignId: '',
    templateId: 'NT003',
  });
  const [sent,    setSent]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [liveLearners, setLiveLearners] = useState([]);
  const [loadingLiveLearners, setLoadingLiveLearners] = useState(true);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkSending, setBulkSending] = useState(false);

  const upd = patch => setForm(f => ({ ...f, ...patch }));

  const audienceOptions = [
    { value: 'all_overdue', label: 'All Overdue Learners',    count: learners.filter(l => l.trainingStatus === 'Overdue').length    },
    { value: 'not_started', label: 'Training Not Started',    count: learners.filter(l => l.trainingStatus === 'Not Started').length },
    { value: 'at_risk',     label: 'At Risk Learners',        count: learners.filter(l => l.trainingStatus === 'At Risk').length     },
    { value: 'specific',    label: 'Specific Learner',        count: null },
  ];

  const getTargetLearners = () => {
    if (form.audience === 'specific') return learners.filter(l => l.id === form.learnerId);
    if (form.audience === 'all_overdue') return learners.filter(l => l.trainingStatus === 'Overdue');
    if (form.audience === 'not_started') return learners.filter(l => l.trainingStatus === 'Not Started');
    if (form.audience === 'at_risk')     return learners.filter(l => l.trainingStatus === 'At Risk');
    return [];
  };

  const handleSend = async () => {
    const targets  = getTargetLearners();
    const template = notificationTemplates.find(t => t.id === form.templateId);
    if (!targets.length || !template) return;

    setLoading(true);
    setSent(null);

    const campaign = trainingCampaigns.find(c => c.id === form.campaignId) || trainingCampaigns[0];
    const recipients = targets.map(l =>
      buildEmailFromTemplate(template, l, {
        trainingName: campaign?.campaignName || 'Security Training',
        dueDate:      campaign?.endDate      || 'N/A',
        daysLeft:     '7',
        daysOverdue:  '3',
        completionPct:'0',
      })
    );

    const result = await sendBulkEmails(recipients);
    setSent({ ...result, template: template.name, simulated: result.results[0]?.simulated });
    setLoading(false);
  };

  const loadLiveLearners = async () => {
    setLoadingLiveLearners(true);
    const response = await dataFetch('read/employees?limit=500');
    setLiveLearners(response?.status === 'success' ? (response?.data ?? []) : []);
    setLoadingLiveLearners(false);

    if (response?.status !== 'success') {
      setBulkResult({ type: 'error', message: response?.error || 'Unable to load learners from the API.' });
    }
  };

  useEffect(() => {
    loadLiveLearners();
  }, []);

  const notStartedLiveLearners = useMemo(() =>
    (liveLearners ?? []).filter(learner => (learner?.trainingStatus ?? '').toLowerCase() === 'not started'),
  [liveLearners]);

  const handleSendAllNotStarted = async () => {
    setBulkSending(true);
    setBulkResult(null);

    const sentTodayResponse = await dataFetch('read/campaign_reminder_sent?limit=500');
    if (sentTodayResponse?.status !== 'success') {
      setBulkSending(false);
      setBulkResult({ type: 'error', message: sentTodayResponse?.error || 'Unable to verify today’s reminder activity.' });
      return;
    }

    const date = new Date().toISOString().slice(0, 10);
    const alreadySent = new Set(
      (sentTodayResponse?.data ?? [])
        .filter(reminder => (reminder?.date ?? '').slice(0, 10) === date)
        .map(reminder => String(reminder?.employee_id ?? ''))
    );
    const targets = notStartedLiveLearners.filter(learner =>
      learner?.email && !alreadySent.has(String(learner?.id ?? ''))
    );

    if (!targets.length) {
      setBulkSending(false);
      setBulkResult({
        type: 'warning',
        message: notStartedLiveLearners.length
          ? 'All not started learners have already received a reminder today.'
          : 'There are no learners with training not started.'
      });
      return;
    }

    const template = notificationTemplates.find(t => t.id === form.templateId) || notificationTemplates.find(t => t.id === 'NT001');
    const campaign = trainingCampaigns.find(c => c.id === form.campaignId) || trainingCampaigns[0];
    const recipients = targets.map(learner => buildEmailFromTemplate(template, learner, {
      trainingName: campaign?.campaignName || 'Security Training',
      dueDate: campaign?.endDate || 'N/A',
      daysLeft: '7',
      daysOverdue: '0',
      completionPct: '0',
    }));

    const result = await sendBulkReminderEmails(recipients);
    const successfulEmails = new Set(
      (result?.results ?? [])
        .filter(email => email?.success)
        .map(email => (email?.email ?? '').toLowerCase())
    );
    const userId = localStorage.getItem('user_id') || '';

    await Promise.all(targets.map(async learner => {
      const delivered = successfulEmails.has((learner?.email ?? '').toLowerCase());

      await dataFetch('create/email_logs_1730000000', {
        method: 'POST',
        body: JSON.stringify({
          email: learner?.email ?? '',
          status: delivered ? 'Delivered' : 'Failed',
          error_message: delivered ? '' : 'Resend delivery failed',
          sent_at: new Date().toISOString(),
          user_id: userId
        })
      });

      if (delivered) {
        await dataFetch('create/campaign_reminder_sent', {
          method: 'POST',
          body: JSON.stringify({
            user_id: userId,
            employee_id: String(learner?.id ?? ''),
            sent_frequency: 1,
            year: new Date().getFullYear(),
            campaign_id: '',
            date
          })
        });
      }
    }));

    window.dispatchEvent(new Event('notification-log-refresh'));
    setBulkSending(false);
    setBulkResult({
      type: result?.failed ? 'warning' : 'success',
      sent: result?.sent ?? 0,
      failed: result?.failed ?? targets.length,
      skipped: notStartedLiveLearners.length - targets.length,
      template: template?.name || 'Selected reminder'
    });
  };

  const selectedTemplate = notificationTemplates.find(t => t.id === form.templateId);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-5">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
          <SafeIcon icon={FiSend} className="text-blue-600 text-base" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Send Manual Reminder</h3>
          <p className="text-xs text-slate-500">Send a one-time notification outside the automated schedule</p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Audience */}
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-2">Target Audience</label>
          <div className="grid grid-cols-2 gap-2">
            {audienceOptions.map(a => (
              <button
                key={a.value}
                onClick={() => upd({ audience: a.value })}
                className={`text-left p-3 rounded-xl border transition-all ${form.audience === a.value ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-300'}`}
              >
                <p className={`text-xs font-semibold ${form.audience === a.value ? 'text-blue-700' : 'text-slate-700'}`}>{a.label}</p>
                {a.count !== null && <p className="text-xs text-slate-400 mt-0.5">{a.count} learner{a.count !== 1 ? 's' : ''}</p>}
              </button>
            ))}
          </div>
        </div>

        {/* Specific learner */}
        {form.audience === 'specific' && (
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Select Learner</label>
            <div className="relative">
              <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              <select
                value={form.learnerId}
                onChange={e => upd({ learnerId: e.target.value })}
                className="w-full pl-8 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:outline-none focus:border-blue-500"
              >
                <option value="">Choose a learner…</option>
                {learners.map(l => <option key={l.id} value={l.id}>{l.firstName} {l.lastName} — {l.email}</option>)}
              </select>
            </div>
          </div>
        )}

        {/* Template */}
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">Email Template</label>
          <div className="relative">
            <SafeIcon icon={FiFileText} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            <select
              value={form.templateId}
              onChange={e => upd({ templateId: e.target.value })}
              className="w-full pl-8 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:outline-none focus:border-blue-500"
            >
              {notificationTemplates.filter(t => t.language === 'en').map(t =>
                <option key={t.id} value={t.id}>{t.name}</option>
              )}
            </select>
          </div>
          {selectedTemplate && (
            <p className="text-xs text-slate-400 mt-1 pl-1">Subject: {selectedTemplate.subject.substring(0, 60)}…</p>
          )}
        </div>

        {/* Email preview */}
        {selectedTemplate && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-slate-500 mb-1.5 flex items-center gap-1.5">
              <SafeIcon icon={FiMail} /> Email Preview
            </p>
            <p className="text-xs text-slate-600 font-medium">Subject: {selectedTemplate.subject}</p>
            <p className="text-xs text-slate-500 mt-1 line-clamp-3 whitespace-pre-line">{selectedTemplate.body}</p>
          </div>
        )}

        {/* Send Button */}
        <button
          onClick={handleSend}
          disabled={loading || (form.audience === 'specific' && !form.learnerId)}
          className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
            !loading && !(form.audience === 'specific' && !form.learnerId)
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed'
          }`}
        >
          {loading
            ? <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 0.8, ease: 'linear' }}><SafeIcon icon={FiSend} /></motion.div>
            : <SafeIcon icon={FiSend} />}
          {loading ? 'Sending Emails…' : 'Send Reminder Emails'}
        </button>
      </div>

      {/* Result */}
      <AnimatePresence>
        {sent && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`rounded-xl p-4 space-y-1 border ${sent.failed === 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}
          >
            <div className={`flex items-center gap-2 font-semibold text-sm ${sent.failed === 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
              <SafeIcon icon={sent.failed === 0 ? FiCheck : FiAlertTriangle} />
              {sent.failed === 0 ? 'Emails Sent Successfully' : `Partial Success`}
            </div>
            <p className={`text-xs ${sent.failed === 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
              {sent.sent} sent · {sent.failed} failed · Template: {sent.template}
            </p>
            {sent.simulated && (
              <p className="text-xs text-slate-500">⚠ Simulated — backend needs RESEND_API_KEY to send real emails.</p>
            )}
            <button onClick={() => setSent(null)} className="text-xs text-slate-400 hover:underline mt-1">Dismiss</button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="border-t border-slate-200 pt-5 space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Send Reminder to All Not Started Learners</h3>
          <p className="text-xs text-slate-500 mt-1">
            {loadingLiveLearners ? 'Loading live learners…' : `${notStartedLiveLearners.length} learners have not started training`}
          </p>
        </div>
        <button onClick={loadLiveLearners} disabled={loadingLiveLearners || bulkSending} className="text-xs text-blue-600 hover:underline disabled:text-slate-400">
          Refresh learner list
        </button>
        <button
          onClick={handleSendAllNotStarted}
          disabled={loadingLiveLearners || bulkSending || notStartedLiveLearners.length === 0}
          className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
            !loadingLiveLearners && !bulkSending && notStartedLiveLearners.length > 0
              ? 'bg-violet-600 hover:bg-violet-700 text-white'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed'
          }`}
        >
          {bulkSending
            ? <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 0.8, ease: 'linear' }}><SafeIcon icon={FiSend} /></motion.div>
            : <SafeIcon icon={FiSend} />}
          {bulkSending ? 'Sending Reminder Emails…' : 'Send Reminder to All Not Started Learners'}
        </button>
        <AnimatePresence>
          {bulkResult && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className={`rounded-xl p-4 space-y-1 border ${
                bulkResult.type === 'success' ? 'bg-emerald-50 border-emerald-200' :
                bulkResult.type === 'error' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
              }`}>
              <div className={`flex items-center gap-2 font-semibold text-sm ${
                bulkResult.type === 'success' ? 'text-emerald-700' :
                bulkResult.type === 'error' ? 'text-red-700' : 'text-amber-700'
              }`}>
                <SafeIcon icon={bulkResult.type === 'success' ? FiCheck : FiAlertTriangle} />
                {bulkResult.type === 'success' ? 'Reminder Emails Sent' : bulkResult.type === 'error' ? 'Unable to Send Reminders' : 'Reminder Send Completed'}
              </div>
              {bulkResult.message ? (
                <p className="text-xs text-slate-600">{bulkResult.message}</p>
              ) : (
                <p className="text-xs text-slate-600">
                  {bulkResult.sent} sent · {bulkResult.failed} failed · {bulkResult.skipped} already sent today · Template: {bulkResult.template}
                </p>
              )}
              <button onClick={() => setBulkResult(null)} className="text-xs text-slate-400 hover:underline mt-1">Dismiss</button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SendReminderPanel;